<script setup lang="ts">
  // POS V2 member identification for earn attach. Accepts the rotating
  // 6-digit code (auto-resolves at 6 digits — wedge scanners type
  // digits+Enter) or a name/phone/member-number lookup. Two hosts:
  // - Draft top bar (no orderId): resolves IClubMemberIdentification | null
  //   (member + the live code); the draft keeps the code and the create
  //   consumes it.
  // - Order view (orderId): the confirm links the member to the order on the
  //   server right away (consuming the code) and resolves
  //   IClubOrderMemberContext | null.
  // Opened via showDynamicDialog(..., 'club-attach').
  import axios from 'axios';
  import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
  import { useDebounceFn } from '@vueuse/core';
  import { ClubService } from '@/api/services/club.service';
  import { formatClubCode } from '@/domain/club/club-program-math';
  import { PermissionType } from '@/enums/PermissionType';
  import {
    IClubMember,
    IClubMemberIdentification,
  } from '@/interfaces/IClubMember';
  import { useAuth } from '@/composition/use-auth';
  import { useModal } from '@/composition/use-modal';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { useUi } from '@/composition/use-ui';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import V2ModalClubTopup from './V2ModalClubTopup.vue';

  const props = withDefaults(
    defineProps<{
      /** Order view host: the confirm links the member to this order. */
      orderId?: number | null;
    }>(),
    { orderId: null }
  );

  const auth = useAuth();
  const modals = useModal();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const money = usePosMoney();

  /** POS users without a club permission would only get a 401 from topup. */
  const canTopup = computed(
    () =>
      auth.hasPermission(PermissionType.ClubTopup) ||
      auth.hasPermission(PermissionType.ClubManage)
  );

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const restaurantId = computed(() => restaurantStore.restaurant?.id ?? null);
  const balanceEnabled = computed(
    () => restaurantStore.restaurant?.club?.balanceEnabled === true
  );

  const mode = ref<'code' | 'lookup'>('code');
  const selected = ref<IClubMember | null>(null);
  // The live code that resolved `selected` (null for lookup picks). Consumed
  // server-side when the order is created / attached with it.
  const selectedCode = ref<string | null>(null);

  function select(member: IClubMember, code: string | null) {
    selected.value = member;
    selectedCode.value = code;
  }

  //#region Code entry (auto-resolve at 6 digits)

  const codeInput = ref('');
  const resolving = ref(false);
  const codeError = ref<string | null>(null);

  // Guards against a stale response landing after the input changed.
  let resolveSeq = 0;

  const codeWrapRef = ref<HTMLElement>();

  function focusCodeInput() {
    codeWrapRef.value?.querySelector('input')?.focus();
  }

  onMounted(focusCodeInput);

  function onCodeInput(value: string) {
    codeError.value = null;
    codeInput.value = value.replace(/\D+/g, '').slice(0, 6);
    if (codeInput.value.length === 6) void resolveCode();
  }

  async function resolveCode() {
    if (codeInput.value.length !== 6 || resolving.value) return;
    if (!restaurantId.value) return;

    const seq = ++resolveSeq;
    resolving.value = true;
    try {
      // identify is a pure preview (never consumes, never grants): the code
      // itself is handed to the host, whose create / attach consumes it.
      const member = await ClubService.identify(
        restaurantId.value,
        codeInput.value
      );
      if (seq !== resolveSeq) return;
      select(member, codeInput.value);
    } catch (e) {
      if (seq !== resolveSeq) return;
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        codeError.value = i18nT('posV2.clubInvalidCode');
        codeInput.value = '';
        focusCodeInput();
      } else {
        ui.serverError(e, i18nT('posV2.clubResolveError'));
      }
    } finally {
      if (seq === resolveSeq) resolving.value = false;
    }
  }

  //#endregion

  //#region Lookup (name / phone / member number)

  const searchInput = ref('');
  const searching = ref(false);
  const searched = ref(false);
  const results = ref<IClubMember[]>([]);

  let searchSeq = 0;

  const runSearch = useDebounceFn(async () => {
    const term = searchInput.value.trim();
    if (term.length < 2 || !restaurantId.value) return;

    const seq = ++searchSeq;
    searching.value = true;
    try {
      const found = await ClubService.lookup(restaurantId.value, {
        search: term,
      });
      if (seq !== searchSeq) return;
      results.value = found;
      searched.value = true;
    } catch (e) {
      if (seq !== searchSeq) return;
      ui.serverError(e, i18nT('posV2.clubSearchError'));
    } finally {
      if (seq === searchSeq) searching.value = false;
    }
  }, 350);

  watch(searchInput, (term) => {
    if (term.trim().length < 2) {
      searchSeq++;
      results.value = [];
      searched.value = false;
      searching.value = false;
      return;
    }
    void runSearch();
  });

  //#endregion

  function switchMode(next: 'code' | 'lookup') {
    mode.value = next;
    codeError.value = null;
    if (next === 'code') {
      codeInput.value = '';
      // Wait for the input to render before focusing.
      void Promise.resolve().then(focusCodeInput);
    }
  }

  function backToSearch() {
    selected.value = null;
    selectedCode.value = null;
    if (mode.value === 'code') {
      codeInput.value = '';
      void Promise.resolve().then(focusCodeInput);
    }
  }

  function openTopup() {
    const program = restaurantStore.restaurant?.club;
    if (!selected.value || !program) return;

    modals.show(
      V2ModalClubTopup,
      { member: selected.value, program },
      {
        name: 'v2-club-topup',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        width: 480,
        maxWidth: Math.round(window.innerWidth * 0.95),
        clickToClose: false,
      },
      {
        'topped-up': (updated: IClubMember) => {
          selected.value = updated;
        },
      }
    );
  }

  const attaching = ref(false);

  function cancel() {
    modals.dialogSubmit('club-attach', null);
  }

  /** Draft host: hand member + code over; order host: link on the server. */
  function confirm() {
    if (!selected.value) return;
    if (props.orderId == null) {
      const result: IClubMemberIdentification = {
        member: selected.value,
        code: selectedCode.value,
      };
      modals.dialogSubmit('club-attach', result);
      return;
    }
    void attachToOrder(props.orderId, selected.value);
  }

  async function attachToOrder(orderId: number, member: IClubMember) {
    if (attaching.value) return;
    attaching.value = true;
    try {
      const context = await ClubService.attachToOrder(orderId, {
        memberId: member.id,
        code: selectedCode.value ?? undefined,
      });
      // Submitting unmounts this modal: no local state writes past here.
      modals.dialogSubmit('club-attach', context);
    } catch (e) {
      attaching.value = false;
      ui.serverError(e, i18nT('posV2.clubAttachError'));
      // A rejected code (consumed elsewhere, expired, another member's) is
      // dead: back to the code step instead of re-sending it from the card.
      if (selectedCode.value) backToSearch();
    }
  }
</script>

<template>
  <div class="v2-modal-club-attach">
    <div class="modal-header">
      <h2 class="modal-title">
        <PosUiIcon name="star-circle" class="title-icon" />
        {{ $t('posV2.clubAttachTitle') }}
      </h2>
      <span class="modal-subtitle">{{ $t('posV2.clubAttachSubtitle') }}</span>
    </div>

    <!-- Confirm card -->
    <div v-if="selected" class="member-card">
      <div class="member-identity">
        <span class="member-name">{{ selected.name }}</span>
        <span class="member-number">
          {{ $t('posV2.clubMemberNumber', { number: selected.memberNumber }) }}
        </span>
      </div>
      <div class="member-stats">
        <div class="stat">
          <span class="stat-label">{{ $t('posV2.clubPoints') }}</span>
          <span class="stat-value">{{ selected.pointsBalance }}</span>
        </div>
        <div v-if="balanceEnabled" class="stat">
          <span class="stat-label">{{ $t('posV2.clubBalance') }}</span>
          <span class="stat-value">
            {{ money.formatComputedMoney(selected.cashTotal) }}
          </span>
        </div>
      </div>
      <div class="member-actions">
        <PosUiButton
          color="neutral"
          variant="outline"
          size="lg"
          class="btn-back"
          :disabled="attaching"
          @click="backToSearch"
        >
          {{ $t('posV2.clubOtherMember') }}
        </PosUiButton>
        <PosUiButton
          v-if="balanceEnabled && canTopup"
          color="neutral"
          variant="outline"
          size="lg"
          icon="wallet-plus-outline"
          class="btn-topup"
          :disabled="attaching"
          @click="openTopup"
        >
          {{ $t('club.member.topup') }}
        </PosUiButton>
        <PosUiButton
          color="primary"
          variant="solid"
          size="lg"
          :icon="orderId != null ? 'link-variant' : undefined"
          class="btn-attach"
          :disabled="attaching"
          @click="confirm"
        >
          {{
            $t(orderId != null ? 'posV2.clubAttachToOrder' : 'posV2.clubAttach')
          }}
        </PosUiButton>
      </div>
    </div>

    <!-- Identification -->
    <template v-else>
      <!-- Code mode -->
      <div v-if="mode === 'code'" class="code-section">
        <div ref="codeWrapRef" class="code-input-wrap">
          <PosUiInput
            :value="codeInput"
            :placeholder="$t('posV2.clubCodePlaceholder')"
            :disabled="resolving"
            size="lg"
            icon="qrcode-scan"
            inputmode="numeric"
            :maxlength="6"
            class="code-input"
            @input="onCodeInput"
            @enter="resolveCode"
          />
        </div>

        <div v-if="resolving" class="code-status">
          <PosUiSkeleton class="status-skeleton" />
        </div>
        <p v-else-if="codeError" class="code-error">
          <PosUiIcon name="alert-circle-outline" class="error-icon" />
          {{ codeError }}
        </p>
        <p v-else class="code-hint">{{ $t('posV2.clubCodeHint') }}</p>

        <PosUiButton
          color="neutral"
          variant="soft"
          icon="account-search"
          class="mode-toggle"
          @click="switchMode('lookup')"
        >
          {{ $t('posV2.clubSearchToggle') }}
        </PosUiButton>
      </div>

      <!-- Lookup mode -->
      <div v-else class="lookup-section">
        <PosUiInput
          :value="searchInput"
          :placeholder="$t('posV2.clubSearchPlaceholder')"
          size="lg"
          icon="account-search"
          clearable
          class="lookup-input"
          @input="(v) => (searchInput = v)"
        />

        <div class="lookup-results">
          <template v-if="searching">
            <PosUiSkeleton v-for="n in 3" :key="n" class="result-skeleton" />
          </template>

          <template v-else-if="results.length">
            <button
              v-for="member in results"
              :key="member.id"
              type="button"
              class="result-row"
              @click="select(member, null)"
            >
              <div class="result-main">
                <span class="result-name">{{ member.name }}</span>
                <span class="result-meta">
                  {{
                    $t('posV2.clubMemberNumber', {
                      number: member.memberNumber,
                    })
                  }}
                  <template v-if="member.phone"> · {{ member.phone }}</template>
                </span>
              </div>
              <span class="result-points">
                {{ member.pointsBalance }} {{ $t('posV2.clubPointsSuffix') }}
              </span>
            </button>
          </template>

          <p v-else-if="searched" class="no-results">
            {{ $t('posV2.clubNoResults') }}
          </p>
        </div>

        <PosUiButton
          color="neutral"
          variant="soft"
          icon="qrcode-scan"
          class="mode-toggle"
          @click="switchMode('code')"
        >
          {{ $t('posV2.clubCodeToggle') }}
        </PosUiButton>
      </div>
    </template>

    <div class="modal-footer">
      <PosUiButton
        color="neutral"
        variant="outline"
        size="lg"
        class="btn-cancel"
        :disabled="attaching"
        @click="cancel"
      >
        {{ $t('posV2.cancel') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-modal-club-attach {
    @apply tw-flex tw-w-full tw-flex-col tw-gap-4 tw-p-4;

    .modal-header {
      @apply tw-flex tw-flex-col tw-gap-0.5;

      .modal-title {
        @apply tw-flex tw-items-center tw-gap-1.5 tw-text-lg tw-font-bold tw-text-neutral-900;

        .title-icon {
          @apply tw-text-xl tw-text-orange-600;
        }
      }

      .modal-subtitle {
        @apply tw-text-sm tw-text-neutral-500;
      }
    }

    .member-card {
      @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-p-4;

      .member-identity {
        @apply tw-flex tw-flex-col tw-gap-0.5;

        .member-name {
          @apply tw-text-lg tw-font-bold tw-text-neutral-900;
        }

        .member-number {
          @apply tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-neutral-500;
        }
      }

      .member-stats {
        @apply tw-flex tw-gap-2;

        .stat {
          @apply tw-flex tw-flex-1 tw-flex-col tw-gap-0.5 tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2;

          .stat-label {
            @apply tw-text-xs tw-text-neutral-500;
          }

          .stat-value {
            @apply tw-text-base tw-font-bold tw-text-neutral-900;
          }
        }
      }

      .member-actions {
        @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2;

        :deep(.btn-back),
        :deep(.btn-attach) {
          @apply tw-min-w-[130px];
        }

        // The primary action takes the leftover width: when its longer
        // order-host label wraps, it fills the whole second row.
        :deep(.btn-attach) {
          @apply tw-flex-1;
        }
      }
    }

    .code-section,
    .lookup-section {
      @apply tw-flex tw-flex-col tw-gap-3;
    }

    .code-input-wrap {
      @apply tw-w-full;

      :deep(.code-input .pos-ui-input-field) {
        @apply tw-text-center tw-text-xl tw-font-bold tw-tracking-[0.4em];
      }
    }

    .code-status {
      @apply tw-flex tw-justify-center;

      .status-skeleton {
        @apply tw-h-5 tw-w-40;
      }
    }

    .code-error {
      @apply tw-mb-0 tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-sm tw-font-medium tw-text-red-600;

      .error-icon {
        @apply tw-text-base;
      }
    }

    .code-hint {
      @apply tw-mb-0 tw-text-center tw-text-sm tw-text-neutral-500;
    }

    .mode-toggle {
      @apply tw-self-center;
    }

    .lookup-results {
      @apply tw-flex tw-max-h-[280px] tw-flex-col tw-gap-1 tw-overflow-y-auto;

      .result-skeleton {
        @apply tw-h-14 tw-w-full tw-rounded-lg;
      }

      .result-row {
        @apply tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors hover:tw-bg-neutral-50;

        .result-main {
          @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5;

          .result-name {
            @apply tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;
          }

          .result-meta {
            @apply tw-truncate tw-text-xs tw-text-neutral-500;
          }
        }

        .result-points {
          @apply tw-flex-shrink-0 tw-text-sm tw-font-semibold tw-text-orange-700;
        }
      }

      .no-results {
        @apply tw-mb-0 tw-py-4 tw-text-center tw-text-sm tw-text-neutral-500;
      }
    }

    .modal-footer {
      @apply tw-flex tw-justify-end;
    }
  }
</style>
